import { useState, useEffect } from 'react';
import {
  Flame, Plus, Trash2, Play, Square, Clock, Thermometer,
  Gauge, Nfc, Check, AlertCircle, Package
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { api } from '@/lib/apiClient';
import { useAuth } from '@/contexts/AuthContext';
import { logger } from '@/lib/logger';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

interface Machine {
  id: string;
  name: string;
  nfc_uid: string | null;
  machine_type: string;
}

interface PieceToAdd {
  id: string;
  work_order_id: string;
  order_number: string;
  product_name: string;
  piece_number: number;
  total_pieces: number;
  nfc_uid: string | null;
}

interface CyclePiece {
  piece: PieceToAdd;
  position?: string;
}

interface AutoclaveCycleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  machineId?: string;
  cycleId?: string;
  onCycleComplete?: () => void;
}

export function AutoclaveCycleDialog({
  open,
  onOpenChange,
  machineId: initialMachineId,
  cycleId,
  onCycleComplete
}: AutoclaveCycleDialogProps) {
  const [machines, setMachines] = useState<Machine[]>([]);
  const [selectedMachine, setSelectedMachine] = useState<string>(initialMachineId || '');
  const [availablePieces, setAvailablePieces] = useState<PieceToAdd[]>([]);
  const [cyclePieces, setCyclePieces] = useState<CyclePiece[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Parámetros del ciclo
  const [pressure, setPressure] = useState<string>('');
  const [temperature, setTemperature] = useState<string>('');
  const [durationMinutes, setDurationMinutes] = useState<string>('');
  const [notes, setNotes] = useState('');
  const [recipes, setRecipes] = useState<any[]>([]);
  const [selectedRecipe, setSelectedRecipe] = useState<string>('');

  // Estado del ciclo
  const [cycleStatus, setCycleStatus] = useState<'pending' | 'in_progress' | 'completed'>('pending');
  const [startedAt, setStartedAt] = useState<Date | null>(null);
  const [elapsedTime, setElapsedTime] = useState(0);

  const { user } = useAuth();

  useEffect(() => {
    if (open) {
      fetchMachines();
      fetchRecipes();
      fetchAvailablePieces();
      if (cycleId) {
        fetchCycleData();
      }
    }
  }, [open, cycleId]);

  useEffect(() => {
    if (initialMachineId) {
      setSelectedMachine(initialMachineId);
    }
  }, [initialMachineId]);

  // Timer
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (cycleStatus === 'in_progress' && startedAt) {
      interval = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - startedAt.getTime()) / 1000));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [cycleStatus, startedAt]);

  const fetchRecipes = async () => {
    try {
      const { data, error } = await api
        .from('production_recipes')
        .select('id, name, ramps:production_recipe_ramps(target_temp, pressure_bar, hold_minutes, ramp_order)')
        .eq('is_active', true)
        .order('name');
      if (error) throw error;
      setRecipes(data || []);
    } catch (error) {
      logger.error('Error fetching recipes:', error);
    }
  };

  const applyRecipeParams = () => {
    const recipe = recipes.find((r) => r.id === selectedRecipe);
    if (!recipe) return;
    const ramps = recipe.ramps || [];
    if (ramps.length === 0) {
      toast.info('La receta no tiene rampas definidas');
      return;
    }
    // Usar la rampa con temperatura más alta para los campos principales
    const maxTempRamp = ramps.reduce((a: any, b: any) => ((b.target_temp || 0) > (a.target_temp || 0) ? b : a));
    if (maxTempRamp.target_temp != null) setTemperature(String(maxTempRamp.target_temp));
    if (maxTempRamp.pressure_bar != null) setPressure(String(maxTempRamp.pressure_bar));
    const totalMin = ramps.reduce((s: number, r: any) => s + (r.hold_minutes || 0), 0);
    if (totalMin > 0) setDurationMinutes(String(totalMin));
    toast.success(`Parametros aplicados: ${ramps.length} rampas, ${totalMin} min totales`);
  };

  // Helper para obtener rampas de la receta seleccionada
  const getSelectedRecipeRamps = () => {
    const recipe = recipes.find((r) => r.id === selectedRecipe);
    if (!recipe) return [];
    return (recipe.ramps || []).sort((a: any, b: any) => (a.ramp_order || 0) - (b.ramp_order || 0));
  };

  const fetchMachines = async () => {
    try {
      const { data, error } = await api
        .from('machines')
        .select('id, name, nfc_uid, machine_type')
        .eq('machine_type', 'autoclave')
        .eq('is_active', true);

      if (error) throw error;
      setMachines(data || []);
    } catch (error) {
      logger.error('Error fetching machines:', error);
    }
  };

  const fetchAvailablePieces = async () => {
    try {
      setLoading(true);

      // Obtener piezas que están en estado de vacío completado (listas para autoclave)
      const { data: pieces, error: piecesError } = await api
        .from('work_order_pieces')
        .select('id, work_order_id, piece_number, nfc_uid, status')
        .in('status', ['in_vacuum', 'pending_autoclave'])
        .order('created_at', { ascending: true });

      if (piecesError) throw piecesError;

      if (!pieces || pieces.length === 0) {
        // Si no hay piezas en estado específico, mostrar todas las pendientes
        const { data: allPieces, error: allError } = await api
          .from('work_order_pieces')
          .select('id, work_order_id, piece_number, nfc_uid, status')
          .neq('status', 'completed')
          .neq('status', 'in_autoclave')
          .order('created_at', { ascending: true });

        if (allError) throw allError;

        if (allPieces && allPieces.length > 0) {
          await enrichPiecesWithWorkOrder(allPieces);
        } else {
          setAvailablePieces([]);
        }
      } else {
        await enrichPiecesWithWorkOrder(pieces);
      }
    } catch (error) {
      logger.error('Error fetching available pieces:', error);
    } finally {
      setLoading(false);
    }
  };

  const enrichPiecesWithWorkOrder = async (pieces: any[]) => {
    const workOrderIds = [...new Set(pieces.map(p => p.work_order_id))];

    const { data: workOrders } = await api
      .from('work_orders')
      .select('id, order_number, product_name, quantity')
      .in('id', workOrderIds);

    const woMap = new Map((workOrders || []).map(wo => [wo.id, wo]));

    const enrichedPieces: PieceToAdd[] = pieces.map(piece => {
      const wo = woMap.get(piece.work_order_id);
      return {
        id: piece.id,
        work_order_id: piece.work_order_id,
        order_number: wo?.order_number || 'N/A',
        product_name: wo?.product_name || 'N/A',
        piece_number: piece.piece_number,
        total_pieces: wo?.quantity || 1,
        nfc_uid: piece.nfc_uid
      };
    });

    setAvailablePieces(enrichedPieces);
  };

  const fetchCycleData = async () => {
    // TODO: Cargar datos de un ciclo existente
  };

  const addPieceToCycle = (piece: PieceToAdd) => {
    if (cyclePieces.some(cp => cp.piece.id === piece.id)) {
      toast.error('Esta pieza ya está en el ciclo');
      return;
    }
    setCyclePieces(prev => [...prev, { piece }]);
    setAvailablePieces(prev => prev.filter(p => p.id !== piece.id));
  };

  const removePieceFromCycle = (pieceId: string) => {
    const removed = cyclePieces.find(cp => cp.piece.id === pieceId);
    if (removed) {
      setAvailablePieces(prev => [...prev, removed.piece]);
      setCyclePieces(prev => prev.filter(cp => cp.piece.id !== pieceId));
    }
  };

  const handleStartCycle = async () => {
    if (!selectedMachine) {
      toast.error('Selecciona un autoclave');
      return;
    }
    if (cyclePieces.length === 0) {
      toast.error('Añade al menos una pieza al ciclo');
      return;
    }

    setSaving(true);
    try {
      // Crear el ciclo
      const { data: newCycle, error: cycleError } = await api
        .from('autoclave_cycles')
        .insert({
          machine_id: selectedMachine,
          recipe_id: selectedRecipe || null,
          pressure: pressure ? parseFloat(pressure) : null,
          temperature: temperature ? parseFloat(temperature) : null,
          duration_minutes: durationMinutes ? parseInt(durationMinutes) : null,
          operator_id: user?.id,
          started_at: new Date().toISOString(),
          status: 'in_progress',
          notes
        })
        .select()
        .single();

      if (cycleError) throw cycleError;

      // Añadir las piezas al ciclo
      const piecesData = cyclePieces.map(cp => ({
        cycle_id: newCycle.id,
        work_order_piece_id: cp.piece.id,
        work_order_id: cp.piece.work_order_id,
        position: cp.position
      }));

      const { error: piecesError } = await api
        .from('autoclave_cycle_pieces')
        .insert(piecesData);

      if (piecesError) throw piecesError;

      // Actualizar estado de las piezas
      for (const cp of cyclePieces) {
        await api
          .from('work_order_pieces')
          .update({ status: 'in_autoclave', updated_at: new Date().toISOString() })
          .eq('id', cp.piece.id);
      }

      toast.success('Ciclo de autoclave iniciado');
      setCycleStatus('in_progress');
      setStartedAt(new Date());
    } catch (error) {
      logger.error('Error starting cycle:', error);
      toast.error('Error al iniciar el ciclo');
    } finally {
      setSaving(false);
    }
  };

  const handleCompleteCycle = async () => {
    setSaving(true);
    try {
      // Actualizar el ciclo
      // TODO: Necesitamos el cycleId guardado

      // Actualizar estado de las piezas
      for (const cp of cyclePieces) {
        await api
          .from('work_order_pieces')
          .update({ status: 'in_demolding', updated_at: new Date().toISOString() })
          .eq('id', cp.piece.id);
      }

      toast.success('Ciclo de autoclave completado');
      setCycleStatus('completed');
      onCycleComplete?.();
      onOpenChange(false);
    } catch (error) {
      logger.error('Error completing cycle:', error);
      toast.error('Error al completar el ciclo');
    } finally {
      setSaving(false);
    }
  };

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const selectedMachineData = machines.find(m => m.id === selectedMachine);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Flame className="h-5 w-5 text-orange-500" />
            Ciclo de Autoclave
            {cycleStatus === 'in_progress' && (
              <Badge className="bg-orange-500 ml-2">En Progreso</Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Panel izquierdo: Configuración */}
          <div className="space-y-4">
            {/* Selector de Autoclave */}
            <div className="space-y-2">
              <Label>Autoclave</Label>
              <Select
                value={selectedMachine}
                onValueChange={setSelectedMachine}
                disabled={cycleStatus !== 'pending'}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar autoclave" />
                </SelectTrigger>
                <SelectContent>
                  {machines.map(machine => (
                    <SelectItem key={machine.id} value={machine.id}>
                      {machine.name}
                      {machine.nfc_uid && ' (NFC)'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Receta (opcional) */}
            <div className="space-y-2">
              <Label>Receta (opcional)</Label>
              <Select
                value={selectedRecipe || 'none'}
                onValueChange={(v) => setSelectedRecipe(v === 'none' ? '' : v)}
                disabled={cycleStatus !== 'pending'}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sin receta (datos manuales)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin receta (manual)</SelectItem>
                  {recipes.map((r) => (
                    <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedRecipe && cycleStatus === 'pending' && (
                <>
                  {/* Mostrar todas las rampas de la receta */}
                  {getSelectedRecipeRamps().length > 0 && (
                    <div className="border rounded-md p-2 bg-muted/30 space-y-1">
                      <p className="text-xs font-medium text-muted-foreground mb-1">Rampas de la receta:</p>
                      {getSelectedRecipeRamps().map((ramp: any, idx: number) => (
                        <div key={idx} className="flex items-center justify-between text-xs bg-background rounded px-2 py-1">
                          <span className="font-medium">Rampa {ramp.ramp_order || idx + 1}</span>
                          <div className="flex items-center gap-3 text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Thermometer className="h-3 w-3" />
                              {ramp.target_temp || 0}°C
                            </span>
                            <span className="flex items-center gap-1">
                              <Gauge className="h-3 w-3" />
                              {ramp.pressure_bar || 0} bar
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {ramp.hold_minutes || 0} min
                            </span>
                          </div>
                        </div>
                      ))}
                      <div className="flex justify-between text-xs pt-1 border-t mt-1">
                        <span className="text-muted-foreground">Total:</span>
                        <span className="font-medium">{getSelectedRecipeRamps().reduce((s: number, r: any) => s + (r.hold_minutes || 0), 0)} min</span>
                      </div>
                    </div>
                  )}
                  <Button type="button" size="sm" variant="outline" onClick={applyRecipeParams} className="w-full gap-2">
                    <Thermometer className="h-3 w-3" />
                    Usar parametros de la receta
                  </Button>
                </>
              )}
            </div>

            {/* Parámetros */}
            <Card>
              <CardContent className="pt-4 space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs flex items-center gap-1">
                      <Gauge className="h-3 w-3" />
                      Presión (bar)
                    </Label>
                    <Input
                      type="number"
                      value={pressure}
                      onChange={(e) => setPressure(e.target.value)}
                      placeholder="0.0"
                      step="0.1"
                      disabled={cycleStatus !== 'pending'}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs flex items-center gap-1">
                      <Thermometer className="h-3 w-3" />
                      Temp (°C)
                    </Label>
                    <Input
                      type="number"
                      value={temperature}
                      onChange={(e) => setTemperature(e.target.value)}
                      placeholder="0"
                      disabled={cycleStatus !== 'pending'}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      Duración (min)
                    </Label>
                    <Input
                      type="number"
                      value={durationMinutes}
                      onChange={(e) => setDurationMinutes(e.target.value)}
                      placeholder="0"
                      disabled={cycleStatus !== 'pending'}
                    />
                  </div>
                </div>

                <div className="space-y-1">
                  <Label className="text-xs">Notas</Label>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Observaciones del ciclo..."
                    rows={2}
                    disabled={cycleStatus === 'completed'}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Timer */}
            {cycleStatus === 'in_progress' && (
              <Card className="bg-orange-50 border-orange-200">
                <CardContent className="pt-4">
                  <div className="text-center">
                    <p className="text-sm text-orange-600 mb-1">Tiempo transcurrido</p>
                    <p className="text-3xl font-mono font-bold text-orange-700">
                      {formatTime(elapsedTime)}
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Piezas disponibles */}
            {cycleStatus === 'pending' && (
              <div className="space-y-2">
                <Label className="text-sm">Piezas Disponibles</Label>
                <ScrollArea className="h-[150px] border rounded-md">
                  {loading ? (
                    <div className="p-4 text-center text-muted-foreground">Cargando...</div>
                  ) : availablePieces.length === 0 ? (
                    <div className="p-4 text-center text-muted-foreground text-sm">
                      No hay piezas disponibles para autoclave
                    </div>
                  ) : (
                    <div className="divide-y">
                      {availablePieces.map(piece => (
                        <div
                          key={piece.id}
                          className="p-2 hover:bg-muted/50 flex items-center justify-between"
                        >
                          <div>
                            <p className="font-medium text-sm">{piece.order_number}</p>
                            <p className="text-xs text-muted-foreground">
                              {piece.product_name} - Pieza {piece.piece_number}/{piece.total_pieces}
                            </p>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => addPieceToCycle(piece)}
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </div>
            )}
          </div>

          {/* Panel derecho: Piezas en el ciclo */}
          <div className="space-y-2">
            <Label className="text-sm flex items-center gap-2">
              <Package className="h-4 w-4" />
              Piezas en este ciclo ({cyclePieces.length})
            </Label>
            <ScrollArea className="h-[400px] border rounded-md">
              {cyclePieces.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  <Package className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p className="font-medium">Sin piezas</p>
                  <p className="text-sm">Añade piezas del panel izquierdo</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>OT / Pieza</TableHead>
                      <TableHead>Producto</TableHead>
                      {cycleStatus === 'pending' && <TableHead className="w-10"></TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {cyclePieces.map(cp => (
                      <TableRow key={cp.piece.id}>
                        <TableCell>
                          <p className="font-medium">{cp.piece.order_number}</p>
                          <p className="text-xs text-muted-foreground">
                            Pieza {cp.piece.piece_number}/{cp.piece.total_pieces}
                          </p>
                        </TableCell>
                        <TableCell className="text-sm">
                          {cp.piece.product_name}
                        </TableCell>
                        {cycleStatus === 'pending' && (
                          <TableCell>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7 text-red-500"
                              onClick={() => removePieceFromCycle(cp.piece.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </ScrollArea>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {cycleStatus === 'completed' ? 'Cerrar' : 'Cancelar'}
          </Button>

          {cycleStatus === 'pending' && (
            <Button
              onClick={handleStartCycle}
              disabled={saving || cyclePieces.length === 0 || !selectedMachine}
              className="bg-orange-600 hover:bg-orange-700"
            >
              <Play className="h-4 w-4 mr-2" />
              {saving ? 'Iniciando...' : 'Iniciar Ciclo'}
            </Button>
          )}

          {cycleStatus === 'in_progress' && (
            <Button
              onClick={handleCompleteCycle}
              disabled={saving}
              className="bg-green-600 hover:bg-green-700"
            >
              <Square className="h-4 w-4 mr-2" />
              {saving ? 'Finalizando...' : 'Finalizar Ciclo'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
